"use client";

import { APIProvider } from "@vis.gl/react-google-maps";
import Script from "next/script";
import { useRef, useEffect, useState } from "react";
import type { ReactNode } from "react";

import { DEBUG_MAPS_API } from "@/lib/environments";

// Extend Window interface to include our callback
declare global {
  interface Window {
    initGoogleMaps?: () => void;
  }
}

interface GoogleMapsProviderProps {
  children: ReactNode;
}

/**
 * Utility function to check if Google Maps API is fully ready
 * This should be used in all map components to prevent rendering before the API is ready
 */
export function isGoogleMapsReady(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.google?.maps &&
    typeof window.google.maps.Map === "function"
  );
}

/**
 * A React context provider that ensures Google Maps is loaded exactly once,
 * even in StrictMode's double-render scenarios.
 */
export function GoogleMapsProvider({ children }: GoogleMapsProviderProps) {
  // Track if we're on the client side and if Maps is ready
  const [isClient, setIsClient] = useState(false);
  const [isMapsReady, setIsMapsReady] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);

  // Use refs to track initialization state across re-renders
  const initializationRef = useRef({
    hasStartedLoading: false,
    error: null as unknown,
  });

  // Effect to handle client-side initialization
  useEffect(() => {
    setIsClient(true);

    // Define the initialization callback
    window.initGoogleMaps = () => {
      if (DEBUG_MAPS_API) {
        console.log("Maps initialization callback triggered");
      }
      if (isGoogleMapsReady()) {
        setIsMapsReady(true);
      }
    };

    // If Maps is already available globally, mark as ready
    if (isGoogleMapsReady()) {
      setIsMapsReady(true);
    }

    return () => {
      // Cleanup
      delete window.initGoogleMaps;
    };
  }, []);

  // Don't render anything special during SSR
  if (!isClient) {
    return children;
  }

  // If Maps is ready, wrap children in APIProvider
  const shouldWrapWithApiProvider = isMapsReady && !loadError;

  return (
    <>
      {shouldWrapWithApiProvider ? (
        <APIProvider
          apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ""}
          onLoad={() => {
            if (DEBUG_MAPS_API) {
              console.log("Google Maps API provider initialized");
            }
          }}
          onError={(error: unknown) => {
            console.error("Google Maps API error:", error);
            initializationRef.current.error = error;
            setLoadError(error);
          }}
          libraries={["places"]}
        >
          {children}
        </APIProvider>
      ) : (
        children
      )}

      {/* If we're not wrapping with APIProvider yet and haven't errored, try to load the script non-blockingly 
        v parameter - https://developers.google.com/maps/documentation/javascript/versions
      */}
      {!shouldWrapWithApiProvider && !loadError && (
        <Script
          id="google-maps"
          src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=places&v=quarterly&map_ids=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_BROWSE_MAP_ID},${process.env.NEXT_PUBLIC_GOOGLE_MAPS_LISTING_DETAIL_MAP_ID}&callback=initGoogleMaps`}
          strategy="lazyOnload"
          onLoad={() => {
            if (DEBUG_MAPS_API) {
              console.log("Google Maps script loaded");
            }
          }}
          onError={(e) => {
            console.error("Google Maps script error:", e);
            initializationRef.current.error = e;
            setLoadError(e);
          }}
        />
      )}
    </>
  );
}
